import {BookingsState} from "./graphql/queries"
import {fromNullable, isNone, isSome, None, none, Option, Some, some} from "fp-ts/lib/Option"
import {Either, left, right} from "fp-ts/lib/Either"

// TypeScript's top type
// ("any" is the dynamic type, not the top type)
export type mixed = {} | null | undefined | void

export function assertNever(x: never): never {
  throw new Error("Unexpected value: " + x)
}

// Note:
// - This does not check if the object is sealed or frozen.
// - Object.keys() excludes enumerable properties, but Object.getOwnPropertyNames() does not.
// - We need to check for symbols as well as properties.
// - Object.getPrototypeOf() is the correct way to get an object's concrete class. The result
// of Object.constructor can be easily forged. Example:
// function Foo() {}
// Foo.prototype.constructor = Object;
// (new Foo()).constructor === Object // true
export const isEmptyObject = (obj: object) =>
  Object.getOwnPropertyNames(obj).length === 0 &&
  Object.getOwnPropertySymbols(obj).length === 0 &&
  Object.getPrototypeOf(obj) === Object.prototype

export function containsDuplicates<T>(array: Array<T>) {
  return array.length !== new Set(array).size
}

export function assertNumber(x: mixed): number {
  if (typeof x === "number") {
    return x
  }
  throw new TypeError("Expected a number")
}

export function assertString(x: mixed): string {
  if (typeof x === "string") {
    return x
  }
  throw new TypeError("Expected a string")
}

export function assertBookingState(x: mixed): BookingsState {
  if (typeof x === "string" && BookingsState.hasOwnProperty(x)) {
    return x as BookingsState
  }
  throw new TypeError("Expected a BookingState, got a " + typeof x)
}

export function assertNonNull<T>(x: T | null | undefined): T {
  if (x === null || x === undefined) {
    // Will render as "Unexpected null" or "Unexpected undefined"
    throw new TypeError("Unexpected " + x)
  }
  return x
}

export function delay(millis: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve()
    }, millis)
  })
}

export type Loading<T> = Option<T>
export const ready = <T>(value: T): Loading<T> => some(value)
export const loading: Loading<never> = none
export const loadingIfNull = <T>(value: T | null | undefined): Loading<T> => fromNullable(value)
export const isReady = <T>(x: Loading<T>): x is Some<T> => isSome(x)
export const isLoading = <T>(x: Loading<T>): x is None<T> => isNone(x)

// Throw the left side of an Either (from a value)
// noinspection JSUnusedGlobalSymbols
export const orThrow = <T>(x: Either<mixed, T>): T =>
  x.fold((e) => {
    throw e
  }, (x) => x)

// Throw the left side of an Either (from a Promise)
// noinspection JSUnusedGlobalSymbols
export const orThrowPromise = async <T>(p: Promise<Either<mixed, T>>): Promise<T> =>
  (await p).fold<T | PromiseLike<T>>(async (e) => {
    throw e
  }, (x) => x)

// Sometimes typescript types [a,b] as (A|B)[] instead of [A,B]. This forces the latter.
export const pair = <A, B>(a: A, b: B): [A, B] => [a, b]

export const mapNullable = <T, U>(x: T | null, f: (x: T) => U | null) => (x === null ? null : f(x))

export function arraySet<T>(a: Array<T>, index: number, value: T): Array<T> {
  index = fixIndex(index, a)
  return [...a.slice(0, index), value, ...a.slice(index + 1)]
}

export function arrayDelete<T>(a: Array<T>, index: number): Array<T> {
  index = fixIndex(index, a)
  return [...a.slice(0, index), ...a.slice(index + 1)]
}

// noinspection JSUnusedGlobalSymbols
export function arrayInsert<T>(a: Array<T>, index: number, value: T): Array<T> {
  index = fixIndex(index, a)
  return [...a.slice(0, index), value, ...a.slice(index)]
}

function fixIndex(index: number, array: Array<mixed>): number {
  if (index < 0) {
    index += array.length
  }
  if (index < 0 || index >= array.length) {
    throw new Error("index out of bounds")
  }
  return index
}

export const nullableToLeft = <T>(x: T | null | undefined): Either<T, void> =>
  x == null ? right(undefined) : left(x)

// noinspection JSUnusedGlobalSymbols
export const nullableToRight = <T>(x: T | null | undefined): Either<void, T> =>
  x == null ? left(undefined) : right(x)

export const liftOptionInEither = <E, T>(x: Either<E, Option<T>>): Option<Either<E, T>> =>
  x.fold((e) => some(left(e)), (x) => x.map((x) => right(x)))

export const liftEitherInOption = <E, T>(x: Option<Either<E, T>>): Either<E, Option<T>> =>
  x.foldL(() => right(none), (x) => x.map((x) => some(x)))

export const liftPromiseInEither = <E, T>(x: Either<E, Promise<T>>): Promise<Either<E, T>> =>
  x.fold(async (e) => left<E, T>(e), async (x) => right<E, T>(await x))

// noinspection JSUnusedGlobalSymbols
export const liftPromiseInOption = <T>(x: Option<Promise<T>>): Promise<Option<T>> =>
  x.foldL(async () => none, async (x) => some(await x))

// noinspection JSUnusedGlobalSymbols
export const flattenOption = <T>(x: Option<Option<T>>) => x.chain((x) => x)

export const flattenEither = <E, T>(x: Either<E, Either<E, T>>) => x.chain((x) => x)

export const fconst = <T>(x: T) => (y: mixed) => x

export const id = <T>(x: T): T => x

export const undefinedToNull = <T>(x: T | null = null): T | null => x



// WEBPACK FOOTER //
// ./src/tsutil.ts